/**
 * Task Type Configuration — icon and label mapping for AgentTaskType values
 *
 * Provides display metadata for each task type used in TasksView and related components.
 */

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import ViewListIcon from '@mui/icons-material/ViewList';
import ImageIcon from '@mui/icons-material/Image';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import InsightsIcon from '@mui/icons-material/Insights';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ExtensionIcon from '@mui/icons-material/Extension';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LanguageIcon from '@mui/icons-material/Language';
import type { SvgIconComponent } from '@mui/icons-material';

export interface TaskTypeConfigEntry {
  label: string;
  icon: SvgIconComponent;
}

export const TASK_TYPE_CONFIG: Record<string, TaskTypeConfigEntry> = {
  'block-update': { label: 'Block Update', icon: AutoFixHighIcon },
  'create-strategy': { label: 'Create Strategy', icon: AddIcon },
  'generate-strategy': { label: 'Generate Strategy', icon: PsychologyIcon },
  'strategy-welcome-goals': { label: 'Welcome & Goals', icon: WavingHandIcon },
  'strategy-task-blocks': { label: 'Task Blocks', icon: ViewListIcon },
  // G4a: the recovery run that persists a finished generation from its per-block state.
  'strategy-persist-from-task': { label: 'Recover Generation', icon: PsychologyIcon },
  'generate-image': { label: 'Generate Image', icon: ImageIcon },
  'strategy-asset-prompt-regeneration': { label: 'Regenerate Asset Prompt', icon: AutoFixHighIcon },
  'fill-content': { label: 'Fill Content', icon: EditNoteIcon },
  'generate-audiences': { label: 'Generate Audiences', icon: GroupsIcon },
  'generate-actors': { label: 'Generate Actors', icon: PersonSearchIcon },
  'fill-social-proofs': { label: 'Social Proofs', icon: ThumbUpIcon },
  'generate-insights': { label: 'Generate Insights', icon: InsightsIcon },
  'scrape-analyze': { label: 'Scrape & Analyze', icon: TravelExploreIcon },
  // Content-plan v2 task types (Sprint 13)
  'generate-content-plan-topics-batch': { label: 'Topic Backlog Batch', icon: AutoStoriesIcon },
  'content-strategy':                   { label: 'Content Strategy', icon: ExtensionIcon },
  'content-deep-research':              { label: 'Deep Research', icon: SearchIcon },
  'topic-deep-research':                { label: 'Topic Deep Research', icon: SearchIcon },
  'creator-research':                   { label: 'Creator Research', icon: GroupIcon },
  'creator-research-refresh':           { label: 'Creator Refresh', icon: RefreshIcon },
  'generate-topics':                    { label: 'Generate Topics', icon: LightbulbIcon },
  'plan-sprint-topics':                 { label: 'Plan Sprint', icon: ViewListIcon },
  'content-post-generation':            { label: 'Post Generation', icon: ArticleIcon },
  'content-platform-post':              { label: 'Legacy Platform Post', icon: ArticleIcon },
  'post-plan-assets':                   { label: 'Post Assets', icon: ArticleIcon },
  'ad-creative-brief-topic-generation':  { label: 'Ad Brief Topics', icon: LightbulbIcon },
  'ad-creative-research':               { label: 'Ad Research', icon: SearchIcon },
  'ad-creative-angle-generation':        { label: 'Ad Angles', icon: LightbulbIcon },
  'ad-creative-generation':              { label: 'Ad Creative Set', icon: CampaignIcon },
  'ad-creative-source-language-detection': { label: 'Detect Ad Language', icon: LanguageIcon },
  'ad-creative-localization-text':        { label: 'Localize Ad Copy', icon: EditNoteIcon },
  'ad-creative-localization-images':      { label: 'Localize Ad Images', icon: ImageIcon },
  'ad-creative-localization-video':       { label: 'Localize Ad Video', icon: CampaignIcon },
  'ad-creative-launch-plan-generation':  { label: 'Ad Launch Plan', icon: RocketLaunchIcon },
  'ad-creative-export':                  { label: 'Ad Export', icon: ArticleIcon },
  'ad-creative-metric-import':           { label: 'Ad Metrics Import', icon: QueryStatsIcon },
  'ad-creative-optimization-analysis':   { label: 'Ad Optimization', icon: InsightsIcon },
  'custom': { label: 'Custom', icon: ExtensionIcon },
};

/** Status chip colors shared across TaskItem and TasksView components */
export const STATUS_COLORS: Record<string, string> = {
  pending: 'info.main',
  running: 'primary.main',
  retrying: 'warning.main',
  cancelling: 'warning.main',
  completed: 'success.main',
  error: 'error.main',
  cancelled: 'error.main',
  skipped: 'text.disabled',
};

/**
 * Status → MUI Chip `color` prop.
 *
 * Prefer this over `STATUS_COLORS` + a hand-set `color: 'white'`. MUI derives the
 * chip's contrast text from the palette per theme; a hardcoded white label is
 * unreadable in dark mode, where `primary.main` is `#C5B8FF` and success/warning
 * are the light dark-mode variants (see .design/tasks-history-table/DESIGN_BRIEF.md).
 * `skipped` has no semantic palette entry — 'default' renders the neutral chip.
 */
export const STATUS_CHIP_COLORS: Record<
  string,
  'default' | 'primary' | 'info' | 'warning' | 'success' | 'error'
> = {
  pending: 'info',
  running: 'primary',
  retrying: 'warning',
  cancelling: 'warning',
  completed: 'success',
  error: 'error',
  cancelled: 'error',
  skipped: 'default',
};

/** Status display labels */
export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  running: 'Running',
  retrying: 'Retrying',
  cancelling: 'Cancelling',
  completed: 'Completed',
  error: 'Error',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};
